import { useEffect, useState } from "react";
import api from "../api/axiosClient";

/* -------- DTO GROUPING (FLAT → ORDER CARDS) -------- */
const groupByOrderId = (rows = []) => {
  const map = {};

  rows.forEach((row) => {
    if (!map[row.orderId]) {
      map[row.orderId] = {
        orderId: row.orderId,
        createdAt: row.createdAt,
        orderStatus: row.orderStatus,
        razorpayOrderId: row.razorpayOrderId,
        totalAmount: row.totalAmount,
        items: [],
      };
    }

    map[row.orderId].items.push({
      productId: row.productId,
      displayName: row.displayName,
      quantity: row.quantity,
      priceAtPurchase: row.priceAtPurchase,
      subtotal: row.subtotal,
      variantId: row.variantId,
    });
  });

  return Object.values(map);
};

/* ------------------- PAGE ------------------- */
export default function OrderHistory() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const res = await api.get("/orders/history");
      setOrders(groupByOrderId(res.data));
    } catch {
      setError("Failed to load order history");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen px-4 py-10 relative overflow-hidden">
      {/* Background */}
      <div
        className="fixed inset-0 z-0"
        style={{
          background:
            "linear-gradient(135deg, #fbcfe8 0%, #bfdbfe 45%, #d8b4fe 100%)",
        }}
      />

      <div className="relative z-10 max-w-5xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-8">
          Order History
        </h1>

        {/* States */}
        {loading && <p className="text-gray-600">Loading orders…</p>}
        {error && <p className="text-red-500">{error}</p>}

        {!loading && orders.length === 0 && (
          <div className="bg-white/60 backdrop-blur-lg rounded-2xl p-6 shadow">
            You haven’t placed any orders yet.
          </div>
        )}

        {/* Orders */}
        <div className="space-y-6">
          {orders.map((order) => (
            <div
              key={order.orderId}
              className="
                bg-white/60 backdrop-blur-lg
                rounded-2xl shadow-xl
                border border-white/40
                p-6
              "
            >
              {/* Header */}
              <div className="flex flex-wrap justify-between gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-500">Order ID</p>
                  <p className="font-semibold">#{order.orderId}</p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Placed On</p>
                  <p className="font-medium">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold
                      ${
                        order.orderStatus === "DELIVERED"
                          ? "bg-green-100 text-green-600"
                          : order.orderStatus === "CANCELLED"
                          ? "bg-red-100 text-red-600"
                          : "bg-yellow-100 text-yellow-600"
                      }`}
                  >
                    {order.orderStatus}
                  </span>
                </div>
              </div>

              {/* Items */}
              <div className="divide-y">
                {order.items.map((item, idx) => (
                  <div
                    key={idx}
                    className="py-3 flex justify-between items-start"
                  >
                    <div>
                      <p className="font-medium text-gray-800">
                        {item.displayName}
                      </p>
                      <p className="text-sm text-gray-500">
                        Qty {item.quantity} × ₹{item.priceAtPurchase}
                      </p>
                    </div>

                    <div className="font-semibold text-gray-800">
                      ₹{item.subtotal}
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mt-4 pt-4 border-t">
                <p className="text-xs text-gray-500">
                  Razorpay Order ID: {order.razorpayOrderId}
                </p>
                <p className="text-lg font-bold text-gray-900">
                  Total ₹{order.totalAmount}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
